import { useState } from 'react'
import { requestGalleryAccess, verifyGalleryAccess } from '../api/client'
import { useManifest } from '../state/manifest'

/**
 * The gate on a link the studio restricted to the people they invited.
 *
 * Holding the URL is not access here — a forwarded copy reaches this screen,
 * not the photos. The visitor names their address and, if the studio actually
 * sent to it, a six-digit code confirms they hold it.
 *
 * Two rules the copy has to keep:
 *   · never say whether an address was on the list. "If that address was
 *     invited, a code is on its way" is the same sentence either way, because
 *     a truthful "no" turns this page into a guest-list lookup.
 *   · never imply an account. The client is not signing up for anything; they
 *     are confirming an email they already received.
 */
export function AccessGate({ shareToken }: { shareToken: string }) {
  const load = useManifest((s) => s.load)
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

  const sendCode = async () => {
    setBusy(true)
    setError(null)
    try {
      await requestGalleryAccess(shareToken, email)
      setStep('code')
    } catch {
      setError('That didn’t go through. Check the address and try again.')
    } finally {
      setBusy(false)
    }
  }

  const submitCode = async () => {
    setBusy(true)
    setError(null)
    try {
      await verifyGalleryAccess(shareToken, email, code)
      await load(shareToken)
    } catch {
      setError('That code didn’t work. It may have expired — ask for a new one.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      data-testid="access-gate"
      className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-6"
    >
      <h1 className="text-[20px] font-bold text-[var(--text-primary)]">This gallery is private</h1>
      <p className="mt-1.5 text-[13px] text-[var(--text-tertiary)]">
        {step === 'email'
          ? 'Confirm the email your photographer sent it to.'
          : `If that address was invited, a code is on its way to ${email}.`}
      </p>

      {step === 'email' ? (
        <>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            aria-label="Your email"
            data-testid="access-gate-email"
            className="mt-4 h-12 w-full rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-secondary)] px-3 text-[14px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--text-tertiary)]"
          />
          <button
            type="button"
            onClick={() => void sendCode()}
            disabled={!emailValid || busy}
            data-testid="access-gate-send"
            className="mt-3 h-12 w-full rounded-xl bg-[var(--accent-primary)] text-[14px] font-semibold text-[#0a0a0a] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? 'Sending…' : 'Send me a code'}
          </button>
        </>
      ) : (
        <>
          <input
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="6-digit code"
            autoComplete="one-time-code"
            aria-label="Your code"
            data-testid="access-gate-code"
            className="mt-4 h-12 w-full rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-secondary)] px-3 text-center text-[18px] tracking-[0.3em] text-[var(--text-primary)] outline-none placeholder:tracking-normal placeholder:text-[var(--text-tertiary)] focus:border-[var(--text-tertiary)]"
          />
          <button
            type="button"
            onClick={() => void submitCode()}
            disabled={code.length !== 6 || busy}
            data-testid="access-gate-verify"
            className="mt-3 h-12 w-full rounded-xl bg-[var(--accent-primary)] text-[14px] font-semibold text-[#0a0a0a] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? 'Checking…' : 'Open the gallery'}
          </button>
          <button
            type="button"
            onClick={() => {
              setStep('email')
              setCode('')
              setError(null)
            }}
            data-testid="access-gate-back"
            className="mt-2 h-10 text-[13px] text-[var(--text-tertiary)]"
          >
            Use a different email
          </button>
        </>
      )}

      {error && (
        <p role="alert" className="mt-3 text-[12.5px] text-[var(--accent-danger,#ef4444)]">
          {error}
        </p>
      )}
    </div>
  )
}
